import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "WORK", target: "#work", index: "01" },
  { label: "GALLERY", route: "/gallery", index: "02" },
  { label: "SERVICES", target: "#services", index: "03" },
  { label: "MANIFESTO", target: "#manifesto", index: "04" },
  { label: "ABOUT", target: "#about", index: "05" },
  { label: "CONTACT", target: "#contact", index: "06" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (link) => {
    setOpen(false);
    if (link.route) {
      if (location.pathname !== link.route) navigate(link.route);
      else window.__lenis?.scrollTo(0, { duration: 1.4 });
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => window.__lenis?.scrollTo(link.target, { duration: 1.4 }), 400);
    } else {
      window.__lenis?.scrollTo(link.target, { duration: 1.4 });
    }
  };

  return (
    <>
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-ink/80 backdrop-blur-md border-b border-white/10" : "bg-transparent"
        }`}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.9, ease: [0.16, 1, 0.3, 1] }}
      >
        <nav className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <button
            data-testid="nav-link-home"
            onClick={() => navigate("/")}
            className="font-display font-bold text-xl tracking-wide text-white"
            style={{ fontWeight: 800 }}
          >
            PURSUIT <span className="text-signal">MEDIA</span>
            <span className="hidden sm:inline font-mono text-[10px] tracking-[0.35em] text-smoke ml-3 align-middle">
              STUDIOS
            </span>
          </button>

          <div className="hidden md:flex items-center gap-8 lg:gap-10">
            {LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                onClick={() => go(l)}
                className={`link-sweep font-mono text-[11px] tracking-[0.25em] transition-colors ${
                  location.pathname === l.route
                    ? "text-signal"
                    : "text-white/80 hover:text-white"
                }`}
              >
                <span className="text-signal mr-1.5">{l.index}</span>
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-cta-button"
              onClick={() => go(LINKS.find((l) => l.label === "CONTACT"))}
              className="group font-mono text-[11px] tracking-[0.2em] bg-signal text-white px-5 py-2.5 hover:bg-white hover:text-ink transition-colors duration-300"
            >
              START A PROJECT
            </button>
          </div>

          <button
            data-testid="mobile-menu-button"
            className="md:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            className="fixed inset-0 z-40 bg-ink/95 backdrop-blur-xl flex flex-col justify-center px-8"
            initial={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)" }}
            exit={{ opacity: 0, clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
          >
            {LINKS.map((l, i) => (
              <motion.button
                key={l.label}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                onClick={() => go(l)}
                className="text-left font-display text-5xl font-bold text-white py-4 border-b border-white/10"
                style={{ fontWeight: 800 }}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.07 }}
              >
                <span className="font-mono text-xs text-signal mr-4">{l.index}</span>
                {l.label}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
